import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FileText,
  Target,
  Layers,
  BookOpen,
  Calendar,
  Code,
  Cloud,
  Download,
  Eye,
  X,
  Loader2,
  FileDown,
  FileSpreadsheet,
  Database,
  Network,
  MessageSquare,
  Printer,
  Share2,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const ICON_MAP = { Target, Layers, FileText, BookOpen, Calendar, Code, Cloud, FileSpreadsheet, Database, Network, MessageSquare };

export default function Documentacion() {
  const { hasPerm } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    api.get("/documentos")
      .then((res) => setDocs(res.data.items || []))
      .catch(() => toast.error("No se pudieron cargar los documentos"))
      .finally(() => setLoading(false));
  }, []);

  const openPreview = async (doc) => {
    if (!doc.has_preview) {
      toast.info("Este documento no tiene vista previa. Descárgalo en Word.");
      return;
    }
    setPreviewDoc(doc);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/documentos/${doc.id}/preview`);
      setPreviewContent(res.data);
    } catch {
      toast.error("Error al cargar la vista previa");
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      toast.loading("Preparando descarga...", { id: "dl" });
      const res = await api.get(`/documentos/${doc.id}/descargar`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      // Extraer extensión del nombre del archivo del catálogo (docx_file puede ser .xlsx, .pdf, .docx)
      const ext = (doc.download_url || "").endsWith(".xlsx") ? "xlsx" :
                  (doc.download_url || "").endsWith(".pdf") ? "pdf" : "docx";
      // Fallback: leer del content-disposition o del tipo mime
      const mime = res.data?.type || res.headers?.["content-type"] || "";
      const realExt = mime.includes("spreadsheet") ? "xlsx" :
                      mime.includes("pdf") ? "pdf" : ext;
      link.setAttribute("download", `${doc.title.replace(/[^a-z0-9]/gi, "_")}.${realExt}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Descargado: ${doc.title}`, { id: "dl" });
    } catch {
      toast.error("Error al descargar el documento", { id: "dl" });
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="documentacion-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-mono mb-1">
              Documentación oficial
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>
              Documentación del Proyecto
            </h1>
            <p className="text-gray-600 mt-1 text-sm max-w-2xl">
              Alcance, módulos funcionales y anexos técnicos/contractuales de Alfa-Field Connect. Disponibles
              para vista previa y descarga en formato Word para presentación gerencial.
            </p>
          </div>
          <div className="px-3 py-2 rounded-md bg-[#0A3747] text-[#E0E621] font-mono text-[11px] uppercase tracking-wider">
            TELEALFACOM S.A. · Feb 2026
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <StatCard label="Documentos" value={docs.length || "—"} />
          <StatCard label="Módulos descritos" value="20" />
          <StatCard label="Anexos" value="C → L" />
          <StatCard label="Formato" value=".docx" />
        </div>

        {/* Docs grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {docs.map((doc) => {
              const Icon = ICON_MAP[doc.icon] || FileText;
              return (
                <div
                  key={doc.id}
                  className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition"
                  data-testid={`doc-card-${doc.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-md bg-[#0A3747] text-[#E0E621] flex items-center justify-center flex-shrink-0">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-wider font-mono text-[#0A3747] bg-[#E0E621] px-2 py-0.5 rounded">
                          {doc.tag}
                        </span>
                        <span className="text-[11px] text-gray-500 font-mono">
                          ~{doc.pages} pág · {doc.size_kb} KB
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 leading-snug">{doc.title}</h3>
                      <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{doc.description}</p>

                      <div className="flex flex-wrap gap-2 mt-4">
                        {doc.has_preview && (
                          <button
                            onClick={() => openPreview(doc)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition"
                            data-testid={`doc-preview-${doc.id}`}
                          >
                            <Eye size={14} /> Vista previa
                          </button>
                        )}
                        <button
                          onClick={() => downloadDoc(doc)}
                          disabled={!hasPerm("M20.download")}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0A3747] text-white text-sm hover:bg-[#135166] transition disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`doc-download-${doc.id}`}
                          title={hasPerm("M20.download") ? "Descargar Word" : "Requiere M20.download"}
                        >
                          <Download size={14} /> Descargar Word
                        </button>
                        {hasPerm("M20.print") && (
                          <button
                            onClick={() => { setPreviewDoc(doc); setTimeout(() => window.print(), 500); }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition"
                            data-testid={`doc-print-${doc.id}`}
                            title="Imprimir"
                          >
                            <Printer size={14} /> Imprimir
                          </button>
                        )}
                        {hasPerm("M20.share") && (
                          <button
                            onClick={async () => {
                              const link = `${window.location.origin}/documentacion?doc=${encodeURIComponent(doc.id)}`;
                              try {
                                await navigator.clipboard.writeText(link);
                                toast.success("Link copiado al portapapeles");
                              } catch {
                                toast.error("No se pudo copiar");
                              }
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition"
                            data-testid={`doc-share-${doc.id}`}
                            title="Compartir link"
                          >
                            <Share2 size={14} /> Compartir
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer note */}
        <div className="bg-[#FFFBE6] border border-[#F0D88A] rounded-md p-4 text-sm text-[#5C4500]">
          <strong>Nota:</strong> Estos documentos son parte del entregable contractual. Los anexos C → L
          forman parte integral del contrato principal. Imprime o exporta a PDF desde Word para la presentación.
        </div>
      </div>

      {/* Preview modal */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreviewDoc(null)}
          data-testid="doc-preview-modal"
        >
          <div
            className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-[#0A3747] text-white">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-[#E0E621]">Vista previa</div>
                <div className="font-semibold text-sm">{previewDoc.title}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadDoc(previewDoc)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#E0E621] text-[#0A3747] text-sm font-semibold hover:opacity-90 transition"
                  data-testid="preview-download-btn"
                >
                  <FileDown size={14} /> Descargar Word
                </button>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="p-1.5 rounded-md hover:bg-white/10"
                  data-testid="preview-close-btn"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-6 bg-white">
              {previewLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="animate-spin text-gray-400" />
                </div>
              ) : (
                <article className="doc-prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{previewContent}</ReactMarkdown>
                </article>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-mono">{label}</div>
      <div className="text-2xl font-bold text-[#0A3747] mt-1" style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
    </div>
  );
}
